import type { CSSProperties } from "react";

export interface SegmentItem {
  value: string;
  label: string;
}

export interface SegmentedControlProps {
  items: Array<string | SegmentItem>;
  value?: string;
  onChange?: (value: string) => void;
  size?: "medium" | "small";
  style?: CSSProperties;
}

/** Segmented control — pill background slides to the active segment. */
export function SegmentedControl({ items = [], value, onChange, size = "medium", style }: SegmentedControlProps) {
  const norm: SegmentItem[] = items.map((it) => (typeof it === "string" ? { value: it, label: it } : it));
  const active = value ?? norm[0]?.value;
  const h = size === "small" ? 32 : 40;
  const idx = Math.max(
    0,
    norm.findIndex((it) => it.value === active),
  );
  return (
    <div
      style={{
        position: "relative",
        display: "inline-grid",
        gridTemplateColumns: `repeat(${norm.length}, 1fr)`,
        padding: 3,
        height: h,
        borderRadius: "var(--radius-md)",
        background: "var(--fill-normal)",
        fontFamily: "var(--font-sans)",
        boxSizing: "border-box",
        ...style,
      }}
    >
      <span
        style={{
          position: "absolute",
          top: 3,
          bottom: 3,
          left: `calc(3px + ${idx} * ((100% - 6px) / ${norm.length}))`,
          width: `calc((100% - 6px) / ${norm.length})`,
          borderRadius: "var(--radius-sm)",
          background: "var(--background-elevated-normal)",
          boxShadow: "var(--shadow-emphasize)",
          transition: "left var(--duration-normal) var(--ease-emphasize)",
        }}
      />
      {norm.map((it) => {
        const on = it.value === active;
        return (
          <button
            key={it.value}
            type="button"
            onClick={() => onChange?.(it.value)}
            style={{
              position: "relative",
              zIndex: 1,
              border: "none",
              background: "transparent",
              cursor: "pointer",
              padding: "0 16px",
              fontSize: size === "small" ? "var(--font-size-label-1)" : "var(--font-size-body-2)",
              fontWeight: on ? 600 : 500,
              color: on ? "var(--label-normal)" : "var(--label-alternative)",
              whiteSpace: "nowrap",
              transition: "color var(--duration-fast) var(--ease-standard)",
            }}
          >
            {it.label}
          </button>
        );
      })}
    </div>
  );
}

export default SegmentedControl;
